import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@adiox/ui';
import { useStreamStore } from '../stream/stream';
import {
  INITIAL_BARS,
  PAGE_BARS,
  applyTick,
  mergeLatest,
  prependBars,
  type Bar,
  type Timeframe,
} from './chartModel';

export type BarsStatus = 'loading' | 'ready' | 'error';

/** How many bars to re-read from the server after a reconnect, to repair whatever the live bar missed. */
const CATCH_UP_BARS = 300;
const NO_BARS: Bar[] = [];

/**
 * The bar series of one chart: the newest history from `/bars`, older pages on demand, and a live last bar built from the
 * quote stream. After a lost connection the tail is re-read from the server, which wins over what was built locally.
 */
export function useBars(symbol: string | null, tf: Timeframe) {
  const api = useApi();
  const store = useStreamStore();
  const key = `${symbol ?? ''}|${tf}`;
  // The state belongs to one symbol+timeframe; state left over from another key is simply not shown.
  const [state, setState] = useState<{ key: string; bars: Bar[]; status: BarsStatus; exhausted: boolean }>({
    key,
    bars: [],
    status: 'loading',
    exhausted: false,
  });
  const current =
    state.key === key ? state : { key, bars: NO_BARS, status: 'loading' as const, exhausted: false };
  const paging = useRef(false);
  const [attempt, setAttempt] = useState(0); // bumped by retry() to load the same symbol and timeframe again
  const heldRef = useRef<{ key: string; bars: Bar[]; exhausted: boolean }>(current);
  useEffect(() => {
    heldRef.current = current;
  });

  const update = useCallback(
    (forKey: string, change: (s: typeof state) => typeof state) =>
      setState((prev) =>
        prev.key === forKey
          ? change(prev)
          : change({ key: forKey, bars: [], status: 'loading', exhausted: false }),
      ),
    [],
  );

  const fetchBars = useCallback(
    async (query: { count: number; to?: number }): Promise<Bar[] | null> => {
      if (!symbol) return null;
      const { data } = await api.GET('/api/v1/bars', { params: { query: { symbol, tf, ...query } } });
      return data?.bars ?? null;
    },
    [api, symbol, tf],
  );

  // A new symbol or timeframe (or a retry) starts over.
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    void fetchBars({ count: INITIAL_BARS })
      .then((loaded) => {
        if (cancelled) return;
        update(key, (s) => (loaded ? { ...s, bars: loaded, status: 'ready' } : { ...s, status: 'error' }));
      })
      .catch(() => !cancelled && update(key, (s) => ({ ...s, status: 'error' })));
    return () => {
      cancelled = true;
    };
  }, [symbol, key, fetchBars, update, attempt]);

  /** Loads the bars again after a failure. */
  const retry = useCallback(() => {
    update(key, (s) => ({ ...s, status: 'loading' }));
    setAttempt((n) => n + 1);
  }, [key, update]);

  // The live bar follows the quote stream (fresh quotes only: a stale price must not draw a candle).
  useEffect(() => {
    if (!symbol) return;
    void store.subscribeQuotes([symbol]);
    let lastTick = 0;
    const off = store.subscribe((snapshot) => {
      const quote = snapshot.quotes[symbol];
      if (!quote || quote.t === lastTick || !quote.fresh || !quote.sessionOpen) return;
      lastTick = quote.t;
      update(key, (s) =>
        s.bars.length === 0 ? s : { ...s, bars: applyTick(s.bars, tf, quote.bid, quote.t) },
      );
    });
    return () => {
      off();
      void store.unsubscribeQuotes([symbol]);
    };
  }, [store, symbol, tf, key, update]);

  // After the connection came back, repair the tail from the server.
  useEffect(() => {
    if (!symbol) return;
    let previous = store.getState().hubState;
    let cancelled = false;
    const off = store.subscribe((snapshot) => {
      const now = snapshot.hubState;
      const wasDown = previous !== 'connected';
      previous = now;
      if (now !== 'connected' || !wasDown) return;
      void fetchBars({ count: CATCH_UP_BARS })
        .then((latest) => {
          if (latest && !cancelled) update(key, (s) => ({ ...s, bars: mergeLatest(s.bars, latest) }));
        })
        .catch(() => undefined);
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [store, symbol, key, fetchBars, update]);

  /** Loads the page of history before the oldest bar held. Safe to call repeatedly while scrolling. */
  const loadOlder = useCallback(async () => {
    const held = heldRef.current;
    const first = held.key === key ? held.bars[0] : undefined;
    if (!first || paging.current || held.exhausted) return;
    paging.current = true;
    try {
      const older = await fetchBars({ count: PAGE_BARS, to: first.t - 1 });
      if (!older) return;
      update(key, (s) =>
        older.length === 0 ? { ...s, exhausted: true } : { ...s, bars: prependBars(s.bars, older) },
      );
    } catch {
      // a failed page is retried the next time the user scrolls back
    } finally {
      paging.current = false;
    }
  }, [fetchBars, key, update]);

  return { bars: current.bars, status: current.status, exhausted: current.exhausted, loadOlder, retry };
}
